// ════════════════════════════════════════════════════════════════════
// TERROR — plazaCamera.js   ·   THE PLAZA'S SHARED CAMERA STATE
// ────────────────────────────────────────────────────────────────────
// A dependency-free module. It imports NOTHING — not three, not r3f,
// not React, not firebase. That is the law it exists under:
//
//   ### GAME LOGIC MUST NOT IMPORT A RENDERER TO LEARN WHICH WAY IS
//   ### FORWARD.  logic → data ← renderer.
//
// Plaza.js's ONE movement loop reads `rotateGridInput()`. Plaza3D's
// camera rig writes `setGroundForward()`. Neither imports the other.
//
// This is the Pier's `pierCamera.js` pattern (app-unstable-pierre),
// carried over because the Plaza is the same shape of screen — a tile
// hub you walk — with ONE difference written into `rotateGridInput`:
// the Plaza steps on a GRID (one tile per beat, turn-then-step), so a
// rotated input has to land back on a cardinal. See below.
//
// ── ⭐ THE DEFAULT IS THE IDENTITY ──────────────────────────────────
// The forward starts at (0, -1) — screen-up, tile-north — which is
// EXACTLY what the Plaza assumed before this module existed. So the
// rotation is applied UNCONDITIONALLY in the movement loop, with zero
// risk to the 2D view, which never touches a camera at all: in 2D the
// rig is not mounted, nobody writes the forward, and `rotateGridInput`
// returns its input untouched. PROVEN in Node, not argued:
// `node plazaCamera.selftest.mjs` from the app root, beside the others.
//
// A rig MUST reset the forward on unmount. A yaw left over from the 3D
// view silently rotating input in the 2D view would be worse than a
// crash, because nothing would report it.
//
// ── THE 3D SWITCH ───────────────────────────────────────────────────
// caP'n, 2026-09-16: "leave the plaza exactly how it is … but flip a
// 3D switch." So the switch DEFAULTS TO OFF (2D, byte-for-byte the
// Plaza that shipped), persists per browser, and is read by Plaza.js
// through `subscribe` so a flip re-renders exactly once.
// ════════════════════════════════════════════════════════════════════

// ── THE VIEWS ───────────────────────────────────────────────────────
// A view supplies a distance MULTIPLIER, never a position. Follow is
// "50% closer than whatever the Plaza already thought was right".
export const VIEWS = {
  follow: { id: "follow", label: "FOLLOW", mult: 0.5 },
  trail:  { id: "trail",  label: "TRAIL",  mult: 1.0 },
};
export const VIEW_ORDER = ["follow", "trail"];
export const DEFAULT_VIEW = "trail";

const LS_VIEW = "ie.terror.plazacam";
const LS_3D   = "ie.terror.plaza3d";

// ── forward ─────────────────────────────────────────────────────────
// Tile space: +x is east, +y is SOUTH (screen-down), matching the
// plazaZones grid. The identity is (0,-1) = north = screen-up.
const FWD = { x: 0, y: -1 };

export function setGroundForward(x, y) {
  const m = Math.hypot(x, y);
  if (!m || !isFinite(m)) return;
  FWD.x = x / m;
  FWD.y = y / m;
}
export function resetGroundForward() { FWD.x = 0; FWD.y = -1; }
export function groundForward() { return FWD; }

// ── THE ROTATION — grid flavour ─────────────────────────────────────
// Input is the raw key vector the Plaza has always read: dx ∈ {-1,0,1}
// (D = +1), dy ∈ {-1,0,1} (W = -1, screen-up). Out comes the SAME
// shape — integers on a grid — rotated so that W means "away from the
// camera", always.
//
// HOW IT SNAPS. The rotated vector is continuous; the Plaza steps on
// tiles. Each axis rounds to the nearest integer, so an input that
// lands between two cardinals (the camera at 45°) keeps BOTH axes and
// walks a diagonal — which the 2D loop already allows (it tests
// `isWalkable(nx, ny)` on whatever it is handed). At the identity this
// is exactly the input: (1,0)→(1,0), (1,1)→(1,1). Proven below.
//
// ⚠ ONLY DIRECTION IS CAMERA-RELATIVE. There is no speed term here and
// there must never be one — a camera is for looking; it does not get a
// vote on how fast you walk.
export function rotateGridInput(dx, dy) {
  if (!dx && !dy) return { x: 0, y: 0 };
  const f = FWD;
  const rx = -f.y, ry = f.x;          // right = forward turned 90° CW
  const fwdAmt = -dy;                 // W (dy=-1) → +1 forward
  const x = fwdAmt * f.x + dx * rx;
  const y = fwdAmt * f.y + dx * ry;
  // Snap to the grid. Math.round on ±0.5 is asymmetric in JS
  // (round(-0.5) = -0), so bias by a hair toward the sign.
  const sx = x === 0 ? 0 : Math.sign(x) * Math.round(Math.abs(x) + 1e-9);
  const sy = y === 0 ? 0 : Math.sign(y) * Math.round(Math.abs(y) + 1e-9);
  // A rotated input can never vanish — if both rounded away (only
  // possible for a cardinal at exactly 45°, where |x|=|y|≈0.707 →
  // both round to 1, so it can't; this is belt-and-braces) keep the
  // dominant axis.
  if (!sx && !sy) return Math.abs(x) >= Math.abs(y) ? { x: Math.sign(x) || 0, y: 0 } : { x: 0, y: Math.sign(y) || 0 };
  return { x: sx, y: sy };
}

// ── the persisted view ──────────────────────────────────────────────
let _view = null;
const listeners = new Set();

export function getView() {
  if (_view) return _view;
  let stored = null;
  try { stored = localStorage.getItem(LS_VIEW); } catch { /* private mode */ }
  _view = VIEWS[stored] ? stored : DEFAULT_VIEW;   // validate on read
  return _view;
}
export function setView(id) {
  if (!VIEWS[id]) return;
  _view = id;
  try { localStorage.setItem(LS_VIEW, id); } catch { /* private mode */ }
  bump();
}
export function viewMult() { return VIEWS[getView()].mult; }

// ── THE FREE POSE ───────────────────────────────────────────────────
// A view is a starting framing, not a cage. The instant the player
// drags, the rig stops steering and CARRIES the pose they chose — by
// TRANSLATING it with the character, never by re-aiming it.
let _posed = false;
export function isPosed() { return _posed; }
export function setPosed(v) { if (_posed !== !!v) { _posed = !!v; bump(); } }

// ── DEFAULT IS AN ACTION, NOT A THIRD VIEW ──────────────────────────
// ⚠ A COUNTER, NOT A BOOLEAN. Pressing Default while already on Trail
// must still work; a boolean makes the button silently dead in exactly
// the most likely case.
let _resetCount = 0;
export function resetCamera() { _resetCount++; _posed = false; _focus = null; setView(DEFAULT_VIEW); bump(); }
export function resetToken() { return _resetCount; }

// Selecting a view must ALSO release the pose, or Follow appears to do
// nothing once the camera has been hand-placed, and reads as a bug.
export function chooseView(id) { _posed = false; _focus = null; setView(id); }

// ── THE LOCK-ON (caP'n, 2026-09-23) ─────────────────────────────────
// "when you click an NPC, pet, or interactable (the portal, fountain,
// etc, V'ael included) … have the camera lock on to them and then let
// you zoom in, out, move around on them until you click on something
// else or the camera options."
// A focus is { key, label, get(out) → [x,y,z] world metres, size m }.
// `get` is a reader, not a position, so a WALKING focus (V'ael, a pet)
// stays locked while it moves. Clicking another focusable swaps it;
// clicking empty world, a view button or DEFAULT releases it. Walking
// does NOT release it — the lens stays on them until you let go.
// A counter (not a boolean) marks each NEW lock so the rig reframes once.
let _focus = null;
let _focusCount = 0;
export function getFocus() { return _focus; }
export function focusToken() { return _focusCount; }
export function setFocus(f) { _focus = f || null; _focusCount++; _posed = false; bump(); }
export function clearFocus() { if (_focus) { _focus = null; _posed = false; bump(); } }

// ── THE 3D SWITCH ───────────────────────────────────────────────────
// Off by default. "Leave the plaza exactly how it is" is the ruling;
// the 3D room is the thing you flip ON.
let _plaza3D = null;
export function getPlaza3D() {
  if (_plaza3D !== null) return _plaza3D;
  let stored = null;
  try { stored = localStorage.getItem(LS_3D); } catch { /* private mode */ }
  _plaza3D = stored === "1";
  return _plaza3D;
}
export function setPlaza3D(on) {
  _plaza3D = !!on;
  try { localStorage.setItem(LS_3D, _plaza3D ? "1" : "0"); } catch { /* private mode */ }
  // Leaving 3D must leave no yaw behind for the 2D loop to eat.
  if (!_plaza3D) { resetGroundForward(); _posed = false; _focus = null; }
  bump();
}
export function togglePlaza3D() { setPlaza3D(!getPlaza3D()); }

function bump() { for (const l of listeners) { try { l(); } catch { /* a listener may not break the rest */ } } }
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
